import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DocumentsUploadListComponent from './DocumentsUploadListComponent';
import { DOCUMENT_ASSETS_QUERY, DOCUMENT_PARTNERS_QUERY } from '../queries';
import {
  DELETE_DOCUMENT_ATTACHMENT_MUTATION,
  AVAILABLE_UPLOAD_DOCUMENT_LIST_MUTATION,
  UPLOAD_DOCUMENT_LIST_MUTATION,
  UPDATE_ADDED_DOCUMENT_DATA_MUTATION,
  SAVE_BULK_DOCUMENT_MUTATION,
  CHANGE_BULK_DOCUMENT_ATTACHMENT_STATE_MUTATION,
  ALL_UPLOAD_DOCUMENT_LIST_MUTATION,
  DELETE_DOCUMENT_ATTACHMENT_FILES_MUTATION
} from '../mutations';
import {
  UPLOAD_DOCUMENT_LIST_EXECUTED,
  AVAILABLE_UPLOAD_DOCUMENT_LIST_EXECUTED,
  DOCUMENT_ATTACHMENT_DELETED,
  UPLOAD_DOCUMENTS_DATA_UPDATED,
  UPLOAD_DOCUMENT_EXECUTED,
  DOCUMENT_ATTACHMENT_FILES_DELETED,
  DOCUMENT_ATTACHMENT_STATE_CHANGED,
  DOCUMENT_UPLOADED_WITH_STATE_UPDATE,
  DOCUMENT_UPLOADED,
  DOCUMENTS_NOT_UPLOADED
} from '../subscriptions';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import flow from 'lodash.flow';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useMutation } from '@apollo/client';
import DocumentsAvailableUploadListComponent from './DocumentsAvailableUploadListComponent';
import { changeValueInArray, changeValuesInArray } from 'utils/functions';
import { showError } from 'actions/actionCreators/snackbarActions';
import BulkDocumentsContainer from 'features/bulkDocuments/BulkDocumentsContainer';
import { useToast } from 'utils/hooks';
import { getBulkFilesUrl } from '../actionCreators';
import { FakeText } from '@totalsoft/rocket-ui';
import { PaginationStyled } from './DocumentsStyles';
import MessageBoxModal from './MessageBoxModal';
import { shallowEqual, useSelector } from 'react-redux';
import { targetArea } from 'constants/targetArea';

const useDocumentPartners = (documentId, siteId) => {
  const { data } = useQueryWithErrorHandling(DOCUMENT_PARTNERS_QUERY, {
    variables: { documentId, siteId },
    fetchPolicy: 'network-only'
  });

  return data?.documentPartners ?? [];
};

const useDocumentAssets = (documentId, siteId) => {
  const { data } = useQueryWithErrorHandling(DOCUMENT_ASSETS_QUERY, {
    variables: { documentId, siteId }
  });

  return data?.documentAssets ?? [];
};
const DocumentsUploadContainer = ({ documentId, siteId, documentNumber, generated, forceReloadDocuments }) => {
  const { t, i18n } = useTranslation();
  const [actionsStates, setActionsStates] = useState({
    saving: false,
    readOnlyAll: false,
    item: { attachmentId: null, deleting: false }
  });
  const [localDocuments, setLocalDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [localAvailableUploadDocuments, setLocalAvailableUploadDocuments] = useState([]);
  const [availableUploadDocuments, setAvailableUploadDocuments] = useState([]);
  const [availableUploadDocumentsLoaded, setAvailableUploadDocumentsLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [newStateBulkTransition, setNewStateBulkTransition] = useState(null);
  const [showChangeStateBulkTransitionMessage, setShowChangeStateBulkTransitionMessage] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [showDeleteAttachmentMessage, setShowDeleteAttachmentMessage] = useState(false);
  const [showDeleteAttachmentFilesMessage, setShowDeleteAttachmentFilesMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pager, setPager] = useState({ page: 1, pageSize: 25 });
  const [filters, setFilters] = useState({ fullTextFilter: '', stateName: '', userName: '', mandatory: null });

  const [entities, setEntities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const addToast = useToast();

  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const [filesUploadInProgress, setFilesUploadInProgress] = useState([]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setActionsStates(prev => ({ ...prev, saving: false, readOnlyAll: false }));
      setLoading(false);
    }
  });

  const partners = useDocumentPartners(documentId, siteId);
  const assets = useDocumentAssets(documentId, siteId);

  const [saveBulkDocument] = useMutation(SAVE_BULK_DOCUMENT_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setActionsStates(prev => ({ ...prev, readOnlyAll: false }));
    },
    onCompleted: data => {
      setShowBulkModal(false);
      const { correlationId } = data.bulkUploadDocument;
      storeKey({ correlationId });
    }
  });

  const [loadUploadDocumentList] = useMutation(UPLOAD_DOCUMENT_LIST_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setDocumentsLoaded(false);
    },
    onCompleted: data => {
      const { correlationId } = data.uploadDocumentList;
      storeKey({ correlationId });
    }
  });

  useEffect(() => {
    if (forceReloadDocuments || (!documentsLoaded && generated)) {
      loadUploadDocumentList({
        variables: {
          input: {
            documentId,
            siteId
          }
        }
      });
    }
  }, [loadUploadDocumentList, documentId, siteId, documentsLoaded, generated, forceReloadDocuments]);

  const [loadAvailableUploadDocuments] = useMutation(AVAILABLE_UPLOAD_DOCUMENT_LIST_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setAvailableUploadDocumentsLoaded(false);
    },
    onCompleted: data => {
      const { correlationId } = data.availableUploadDocumentList;
      storeKey({ correlationId });
    }
  });

  const [loadAllUploadDocuments] = useMutation(ALL_UPLOAD_DOCUMENT_LIST_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
    },
    onCompleted: data => {
      const { correlationId } = data.allUploadDocumentList;
      storeKey({ correlationId });
    }
  });

  const [updateAddedDocumentData] = useMutation(UPDATE_ADDED_DOCUMENT_DATA_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setActionsStates(prev => ({ ...prev, saving: false, readOnlyAll: false }));
    },
    onCompleted: r => {
      const { correlationId } = r.updateAddedDocumentData;
      storeKey({ correlationId });
    }
  });

  const [deleteDocumentAttachment] = useMutation(DELETE_DOCUMENT_ATTACHMENT_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setActionsStates(prev => ({ ...prev, saving: false, item: { attachmentId: null, deleting: false } }));
    },
    onCompleted: r => {
      const { correlationId } = r.deleteDocumentAttachment;
      storeKey({ correlationId });
    }
  });

  const [deleteFiles] = useMutation(DELETE_DOCUMENT_ATTACHMENT_FILES_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setActionsStates(prev => ({ ...prev, saving: false, item: { attachmentId: null, deleting: false } }));
    },
    onCompleted: r => {
      const { correlationId } = r.deleteDocumentAttachmentFiles;
      storeKey({ correlationId });
    }
  });

  useEffect(() => {
    const updatedDocuments = documents.map(editableDocument.create);
    updatedDocuments.forEach(doc => {
      doc.document.isSelected = false;
    });
    setLocalDocuments(updatedDocuments.sort((a, b) => (a.document.entityLabel > b.document.entityLabel ? 1 : -1)));
  }, [documentsLoaded, documents]);

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize' ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const updateSelectedLocalDocuments = useCallback(
    documents => {
      const updatedDocuments = documents.map(doc => ({
        ...doc,
        document: {
          ...doc.document,
          isSelected: false
        }
      }));

      setSelectAll(false);
      setLocalDocuments(updatedDocuments);
    },
    [setLocalDocuments, setSelectAll]
  );

  const onFilterChanged = useCallback(
    (prop, value) => {
      setFilters(prevFilter => ({
        ...prevFilter,
        [prop]: value
      }));
      setPager(pagerUpdater('page', 1));

      updateSelectedLocalDocuments(localDocuments);
    },
    [pagerUpdater, updateSelectedLocalDocuments, localDocuments]
  );

  const resetFilters = useCallback(() => {
    setFilters(prevFilter => ({
      ...prevFilter,
      fullTextFilter: '',
      mandatory: null
    }));
    updateSelectedLocalDocuments(localDocuments);
  }, [localDocuments, updateSelectedLocalDocuments]);

  const onChangePage = useCallback(
    page => {
      setPager(pagerUpdater('page', ++page));
    },
    [pagerUpdater]
  );

  const onChangeRowsPerPage = useCallback(
    pageSize => {
      setPager(pagerUpdater('pageSize', pageSize));
    },
    [pagerUpdater]
  );

  const currentPageNumber = useMemo(() => pager.page * pager.pageSize - pager.pageSize, [pager]);

  const getFiltersDocuments = useCallback(() => {
    if (!localDocuments || localDocuments.length === 0) return [];

    const { userName, stateName, fullTextFilter } = filters;
    const stateNames = localDocuments.map(({ document }) => document.stateName);
    if (stateName && !stateNames.includes(stateName)) {
      setFilters(prevFilters => ({
        ...prevFilters,
        stateName: ''
      }));
    }

    const filteredDocuments = localDocuments.filter(({ document }) => {
      const {
        attachmentTypeName,
        contextName,
        entityTitle,
        isMandatory,
        userName: documentUserName,
        stateName: documentStateName
      } = document;
      const searchFilter = fullTextFilter.toLowerCase();

      const containsFullTextFilter =
        attachmentTypeName?.toLowerCase()?.includes(searchFilter) ||
        contextName?.toLowerCase()?.includes(searchFilter) ||
        entityTitle?.toLowerCase()?.includes(searchFilter) ||
        documentUserName?.toLowerCase()?.includes(searchFilter) ||
        documentStateName?.toLowerCase()?.includes(searchFilter);

      return (
        (!userName || documentUserName?.toLowerCase()?.includes(userName.toLowerCase())) &&
        (!stateName || (documentStateName !== null && documentStateName === stateName)) &&
        (filters.mandatory === null || isMandatory === filters.mandatory) &&
        containsFullTextFilter
      );
    });
    return filteredDocuments;
  }, [filters, localDocuments]);

  const getLocalDocumentsPaged = useCallback(() => {
    const pagedDocuments = getFiltersDocuments()
      .slice(currentPageNumber, currentPageNumber + pager.pageSize)
      .map(doc => ({
        ...doc,
        document: {
          ...doc.document,
          isSelected: selectAll ? selectAll : doc.document.isSelected
        }
      }));
    return pagedDocuments;
  }, [currentPageNumber, getFiltersDocuments, pager.pageSize, selectAll]);

  const getLocalStates = useCallback(() => {
    if (!localDocuments || localDocuments?.length === 0) return [];
    const uniqueStates = localDocuments.reduce((acc, { document }) => {
      const stateName = document.stateName;
      if (stateName && !acc.includes(stateName)) {
        acc.push(stateName);
      }
      return acc;
    }, []);

    const states = uniqueStates.map(stateName => ({ stateName }));

    return states;
  }, [localDocuments]);

  const getLocalUsersName = useCallback(() => {
    if (!localDocuments || localDocuments?.length === 0) return [];

    const uniqueUserNames = localDocuments.reduce((acc, { document }) => {
      const userName = document.userName;
      if (userName && !acc.includes(userName)) {
        acc.push(userName);
      }
      return acc;
    }, []);

    const localUsers = uniqueUserNames.map(userName => ({ userName }));

    return localUsers;
  }, [localDocuments]);

  const handleRefresh = useCallback(() => {
    loadUploadDocumentList({
      variables: {
        input: {
          documentId,
          siteId
        }
      }
    });
  }, [documentId, siteId, loadUploadDocumentList]);

  const filterAvailableDocuments = useCallback(
    (availableDocuments, filter) => {
      if (availableUploadDocumentsLoaded)
        return availableDocuments.filter(
          avaDoc =>
            filter === '' ||
            avaDoc.attachmentTypeName.toUpperCase().includes(filter.toUpperCase()) ||
            avaDoc.contextName.toUpperCase().includes(filter.toUpperCase())
        );
      return [];
    },
    [availableUploadDocumentsLoaded]
  );

  useEffect(() => {
    if (availableUploadDocumentsLoaded && assets && partners) {
      let tmpDoc = availableUploadDocuments;

      availableUploadDocuments.forEach(document => {
        if (document.contextId === targetArea.Client)
          tmpDoc = changeValuesInArray(tmpDoc, 'attachmentId', document.attachmentId, {
            entityId: partners[0].partnerId,
            entityTitle: partners[0].partnerName,
            isDocumentPartner: partners[0].isDocumentPartner
          });
        if (document.contextId === targetArea.Asset)
          tmpDoc = changeValuesInArray(tmpDoc, 'attachmentId', document.attachmentId, {
            entityId: assets[0].internalId,
            entityTitle: assets[0].assetFullName
          });
      });

      setLocalAvailableUploadDocuments(tmpDoc);
    }
  }, [availableUploadDocumentsLoaded, assets, partners]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRowSelectionChanged = useCallback(
    (attachmentId, checked) => {
      const res = changeValueInArray(localAvailableUploadDocuments, 'attachmentId', attachmentId, 'selected', checked);
      setLocalAvailableUploadDocuments(res);
    },
    [localAvailableUploadDocuments]
  );

  const onAssetChange = useCallback(
    (attachmentId, documentDetailId, assetFullName) => {
      const res = changeValueInArray(localAvailableUploadDocuments, 'attachmentId', attachmentId, 'entityId', documentDetailId);
      const res2 = changeValueInArray(res, 'attachmentId', attachmentId, 'entityTitle', assetFullName);
      setLocalAvailableUploadDocuments(res2);
    },
    [localAvailableUploadDocuments]
  );

  const onPartnerChange = useCallback(
    (attachmentId, partner) => {
      const res = changeValuesInArray(localAvailableUploadDocuments, 'attachmentId', attachmentId, {
        entityId: partner.partnerId,
        entityTitle: partner.partnerName,
        isDocumentPartner: partner.isDocumentPartner
      });
      setLocalAvailableUploadDocuments(res);
    },
    [localAvailableUploadDocuments]
  );

  const onCloseAvailableUploadListModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const updateAddedDocuments = useCallback(
    (updatedDocuments, addedDocuments) => {
      async function onUpdateAsync() {
        setActionsStates(prev => ({ ...prev, saving: true, readOnlyAll: true }));
        updateAddedDocumentData({
          variables: {
            documentId,
            siteId,
            list: addedDocuments.map(x => x.document)
          }
        });
        updatedDocuments.push(...addedDocuments);
        setLocalDocuments(updatedDocuments.map(flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo)));
      }
      onUpdateAsync();
    },
    [updateAddedDocumentData, documentId, siteId]
  );

  useGlobalSubscription(UPLOAD_DOCUMENTS_DATA_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const modifiedDocuments = data?.uploadDocumentsDataUpdated?.list;

      if (modifiedDocuments && modifiedDocuments.length > 0) {
        const updatedLocalDocuments = localDocuments.map(doc => {
          const modifiedDoc = modifiedDocuments.find(modifiedDoc => modifiedDoc.oldAttachmentId === doc.document.attachmentId);

          if (modifiedDoc) {
            return {
              ...doc,
              document: {
                ...doc.document,
                attachmentId: modifiedDoc.newAttachmentId,
                stateId: modifiedDoc.stateId,
                stateName: modifiedDoc.stateName,
                stateColor: modifiedDoc.stateColor,
                uniqueId: `${modifiedDoc.newAttachmentId}_${modifiedDoc.contextId}`,
                fileId: modifiedDoc.fileId,
                fileName: modifiedDoc.fileName,
                isUploaded: modifiedDoc.isUploaded,
                isMandatory: modifiedDoc.isMandatory,
                uploadDate: modifiedDoc.uploadDate,
                valabilityDate: modifiedDoc.valabilityDate,
                userName: modifiedDoc.userName,
                orderNo: modifiedDoc.orderNo,
                canExpire: modifiedDoc.canExpire,
                entityType: modifiedDoc.entityType,
                entityLabel: extractEntityLabel(modifiedDoc.entityTitle, modifiedDoc.contextId, modifiedDoc.entityType),
                canDeleteFile: modifiedDoc.canDeleteFile,
                attachmentProperties: modifiedDoc.attachmentProperties
              }
            };
          }

          return doc;
        });

        setLocalDocuments(updatedLocalDocuments.sort((a, b) => (a.document.entityLabel > b.document.entityLabel ? 1 : -1)));
        setEntities(updatedLocalDocuments.map(d => d.document.entityLabel).filter((value, index, list) => list.indexOf(value) === index));
      }
      setActionsStates(prev => ({ ...prev, saving: false, readOnlyAll: false }));
    }
  });

  useGlobalSubscription(UPLOAD_DOCUMENT_LIST_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const modifiedDocuments = data.uploadDocumentListExecuted.list.map(l => {
        return {
          ...l,
          entityLabel: extractEntityLabel(l.entityTitle, l.contextId, l.entityType)
        };
      });
      setDocuments(modifiedDocuments.sort((a, b) => (a.entityLabel > b.entityLabel ? 1 : -1)));

      const modifiedEntities = data.uploadDocumentListExecuted.list.map(l => {
        return extractEntityLabel(l.entityTitle, l.contextId, l.entityType);
      });

      const uniqueEntities = modifiedEntities.filter((value, index, list) => list.indexOf(value) === index);

      setEntities(uniqueEntities.sort((a, b) => (a > b ? 1 : -1)));
      setDocumentsLoaded(true);
    }
  });

  const extractEntityLabel = useCallback(
    (entityTitle, contextId, entityType) => {
      if (contextId === targetArea.Client) {
        const context = t(`OfferEdit.Documents.TargetArea.Client`);
        return entityTitle !== null ? `${context} - ${entityTitle}` : context;
      }
      if (contextId === targetArea.Asset) {
        const context = t(`OfferEdit.Documents.TargetArea.Asset`);
        return entityTitle !== null ? `${context} - ${entityTitle}` : context;
      }
      if (contextId === targetArea.Document) {
        const context = t(`OfferEdit.Documents.TargetArea.DOC`);
        const translatedEntityType = t(`OfferEdit.Documents.EntityType.${entityType}`);
        if (i18n.exists(`OfferEdit.Documents.EntityType.${entityType}`)) {
          return entityTitle != null ? `${context} - ${translatedEntityType} ${entityTitle}` : context;
        }
      }
      return entityTitle;
    },
    [i18n, t]
  );

  useGlobalSubscription(UPLOAD_DOCUMENT_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const entityLabel = extractEntityLabel(
        data.uploadDocumentExecuted.newDocument.entityTitle,
        data.uploadDocumentExecuted.newDocument.contextId,
        data.uploadDocumentExecuted.newDocument.entityType
      );
      setLocalDocuments(state => {
        const newDocument = editableDocument.create(data.uploadDocumentExecuted.newDocument);

        const localDocument = {
          ...newDocument,
          document: {
            ...newDocument.document,
            entityLabel: entityLabel
          }
        };
        state.push(localDocument);
        return state;
      });

      setEntities(state => {
        const listHasEntityLabel = state.some(entity => entity === entityLabel);

        if (!listHasEntityLabel) {
          state.push(entityLabel);
          return state.sort((a, b) => (a > b ? 1 : -1));
        }

        return state;
      });
    }
  });

  useGlobalSubscription(DOCUMENT_ATTACHMENT_STATE_CHANGED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setLocalDocuments(state =>
        state.map(item => {
          if (item.document.attachmentId !== data.documentAttachmentStateChanged.documentAttachmentId) return item;

          return {
            ...item,
            document: {
              ...item.document,
              stateId: data.documentAttachmentStateChanged.stateId,
              stateName: data.documentAttachmentStateChanged.stateName,
              stateColor: data.documentAttachmentStateChanged.stateColor,
              canUpload: data.documentAttachmentStateChanged.canUpload,
              canDelete: data.documentAttachmentStateChanged.canDelete,
              canDeleteFile: data.documentAttachmentStateChanged.canDeleteFile,
              isSelected: false,
              saving: false,
              validatePropertiesOnUpload: data.documentAttachmentStateChanged.validatePropertiesOnUpload
            }
          };
        })
      );
      setActionsStates(state => ({ ...state, readOnlyAll: false }));
    }
  });

  useGlobalSubscription(DOCUMENT_UPLOADED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const updatedFiles = filesUploadInProgress.map(state =>
        state.attachmentId === data.documentUploaded.attachmentId ? { ...state, inProgress: false } : state
      );
      setFilesUploadInProgress(updatedFiles);
      setLocalDocuments(state =>
        state.map(item => {
          if (item.document.attachmentId !== data.documentUploaded.attachmentId) return item;

          return {
            ...item,
            document: {
              ...item.document,
              isUploaded: true,
              fileId: data.documentUploaded.fileId,
              userName: data.documentUploaded.userName,
              uploadDate: data.documentUploaded.uploadDate,
              valabilityDate: data.documentUploaded.valabilityDate,
              entityType: data.documentUploaded.entityType,
              entityTitle: data.documentUploaded.entityTitle,
              saving: false
            }
          };
        })
      );
    }
  });

  useGlobalSubscription(DOCUMENTS_NOT_UPLOADED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setActionsStates(prev => ({ ...prev, readOnlyAll: false }));
      addToast(
        t('OfferEdit.Documents.DocumentsNotUploaded', { data: data.documentsNotUploaded.documents }),
        'warning',
        null,
        false,
        false,
        true
      );
    }
  });

  useGlobalSubscription(DOCUMENT_UPLOADED_WITH_STATE_UPDATE, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const updatedFiles = filesUploadInProgress.map(state =>
        state.attachmentId === data.documentUploadedWithStateUpdate.attachmentId ? { ...state, inProgress: false } : state
      );
      setFilesUploadInProgress(updatedFiles);

      setLocalDocuments(state =>
        state.map(item => {
          if (item.document.attachmentId !== data.documentUploadedWithStateUpdate.attachmentId) return item;

          return {
            ...item,
            document: {
              ...item.document,
              isUploaded: true,
              fileId: data.documentUploadedWithStateUpdate.fileId,
              userName: data.documentUploadedWithStateUpdate.userName,
              uploadDate: data.documentUploadedWithStateUpdate.uploadDate,
              valabilityDate: data.documentUploadedWithStateUpdate.valabilityDate,
              stateId: data.documentUploadedWithStateUpdate.stateId,
              stateName: data.documentUploadedWithStateUpdate.stateName,
              stateColor: data.documentUploadedWithStateUpdate.stateColor,
              canUpload: data.documentUploadedWithStateUpdate.canUpload,
              canDelete: data.documentUploadedWithStateUpdate.canDelete,
              canDeleteFile: data.documentUploadedWithStateUpdate.canDeleteFile,
              validatePropertiesOnUpload: data.documentUploadedWithStateUpdate.validatePropertiesOnUpload
            }
          };
        })
      );
    }
  });

  const onAddUploadDocuments = useCallback(() => {
    const addedDocuments = [];
    localAvailableUploadDocuments
      .filter(x => x.selected)
      .forEach(item => {
        let orderNo =
          Math.max(
            ...localDocuments
              .filter(function (document) {
                return document.document.attachmentTypeId === item.attachmentTypeId && document.document.entityTitle === item.entityTitle;
              })
              .map(o => o.document.orderNo)
          ) + 1;
        if (isNaN(orderNo) || orderNo === -Infinity) orderNo = 1;
        const attachmentTypeName = orderNo === 1 ? item.attachmentTypeName : item.attachmentTypeName + '_' + orderNo;

        const newDoc = editableDocument.create({
          ...item,
          orderNo: orderNo,
          attachmentTypeName: attachmentTypeName,
          entityLabel: item.entityTitle ? item.contextName + ' - ' + item.entityTitle : item.contextName
        });
        addedDocuments.push(newDoc);
      });
    const updatedDocuments = localDocuments;

    updateAddedDocuments(updatedDocuments, addedDocuments);

    setLocalAvailableUploadDocuments(
      localAvailableUploadDocuments.map(document => {
        return { ...document, selected: false };
      })
    );

    setShowModal(false);
  }, [localAvailableUploadDocuments, localDocuments, updateAddedDocuments]);

  const onFilterValueChanged = useCallback(value => {
    setFilter(value);
  }, []);

  const onAdd = useCallback(() => {
    if (!availableUploadDocumentsLoaded) {
      loadAvailableUploadDocuments({
        variables: {
          documentId,
          siteId
        }
      });
    }
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAvailableUploadDocuments, documentId, siteId]);

  const onOpenDeleteAttachmentMessage = useCallback(() => {
    setShowDeleteAttachmentMessage(true);
  }, []);

  const onCloseDeleteAttachmentMessage = useCallback(() => {
    setShowDeleteAttachmentMessage(false);
  }, []);

  const onYesDeleteAttachmentMessage = useCallback(() => {
    setActionsStates(prev => ({ ...prev, item: { attachmentId, deleting: true } }));
    onCloseDeleteAttachmentMessage();

    const { attachmentId, fileId } = attachmentToDelete;

    try {
      deleteDocumentAttachment({
        variables: {
          input: { documentId, siteId, documentAttachmentId: attachmentId, fileId }
        }
      });
    } catch (error) {
      showError(error.message);
    }

    setAttachmentToDelete(null);
  }, [attachmentToDelete, deleteDocumentAttachment, documentId, onCloseDeleteAttachmentMessage, siteId]);

  const onDelete = useCallback(
    (attachmentId, fileId) => () => {
      setAttachmentToDelete({ attachmentId, fileId });
      onOpenDeleteAttachmentMessage();
    },
    [onOpenDeleteAttachmentMessage]
  );

  const onOpenDeleteAttachmentFilesMessage = useCallback(() => {
    setShowDeleteAttachmentFilesMessage(true);
  }, []);

  const onCloseDeleteAttachmentFilesMessage = useCallback(() => {
    setShowDeleteAttachmentFilesMessage(false);
  }, []);

  const onYesDeleteAttachmentFilesMessage = useCallback(() => {
    onCloseDeleteAttachmentFilesMessage();

    const { attachmentId, fileId, deleteMyFiles, deleteAllFiles } = attachmentToDelete;
    setActionsStates(prev => ({ ...prev, item: { attachmentId, deleting: true } }));
    try {
      deleteFiles({
        variables: {
          input: { documentId, siteId, userId: userInfo.userId, fileId, deleteMyFiles, deleteAllFiles }
        }
      });
    } catch (error) {
      showError(error.message);
    }

    setAttachmentToDelete(null);
  }, [documentId, siteId, attachmentToDelete, userInfo.userId, onCloseDeleteAttachmentFilesMessage, deleteFiles]);

  const onDeleteFiles = useCallback(
    (attachmentId, fileId, deleteMyFiles, deleteAllFiles) => {
      setAttachmentToDelete({ attachmentId, fileId, deleteMyFiles, deleteAllFiles });
      onOpenDeleteAttachmentFilesMessage();
    },
    [onOpenDeleteAttachmentFilesMessage]
  );

  useGlobalSubscription(DOCUMENT_ATTACHMENT_DELETED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const index = localDocuments.findIndex(x => x.document.attachmentId === data.documentAttachmentDeleted.deletedAttachmentId);
      setLocalDocuments(collection.remove(index, localDocuments));
      setActionsStates(prev => ({ ...prev, item: { attachmentId: null, deleting: false } }));
    }
  });

  useGlobalSubscription(DOCUMENT_ATTACHMENT_FILES_DELETED, {
    variables: { documentId, siteId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (data.documentAttachmentFilesDeleted.deletedLastVersion) {
        const updatedLocalDocuments = localDocuments.map(doc => {
          if (doc.document.fileId === data.documentAttachmentFilesDeleted.fileId) {
            return {
              ...doc,
              document: {
                ...doc.document,
                isUploaded: false,
                isDeleted: true
              }
            };
          }
          return doc;
        });

        setLocalDocuments(updatedLocalDocuments);
      }
      setActionsStates(prev => ({ ...prev, item: { attachmentId: null, deleting: false } }));
    }
  });

  useGlobalSubscription(AVAILABLE_UPLOAD_DOCUMENT_LIST_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setAvailableUploadDocuments(data.availableUploadDocumentListExecuted.list);
      setAvailableUploadDocumentsLoaded(true);
    }
  });

  const changeLocalDocuments = useCallback(
    (currentDocument, attachmentId) => {
      setLocalDocuments(state =>
        state.map(item => {
          if (item.document.attachmentId === attachmentId) {
            return {
              ...item,
              document: {
                ...item.document,
                ...currentDocument.document
              }
            };
          }
          return item;
        })
      );
    },
    [setLocalDocuments]
  );

  const onToggleBulkModal = useCallback(() => {
    loadAllUploadDocuments({
      variables: {
        documentId,
        siteId
      }
    });
    setLoading(true);
    setShowBulkModal(update => !update);
  }, [documentId, siteId, loadAllUploadDocuments]);

  const onSaveDocument = useCallback(
    data => {
      setActionsStates(prev => ({ ...prev, readOnlyAll: true }));
      saveBulkDocument({
        variables: {
          input: {
            model: data,
            documentId,
            siteId
          }
        }
      });
    },
    [documentId, siteId, saveBulkDocument]
  );

  const handleSelectAll = useCallback(() => {
    const updatedLocalDocuments = localDocuments.map(doc => {
      if (getFiltersDocuments().some(pagedDoc => pagedDoc.document.attachmentId === doc.document.attachmentId)) {
        return {
          ...doc,
          document: {
            ...doc.document,
            isSelected: !selectAll
          }
        };
      }
      return doc;
    });

    setLocalDocuments(updatedLocalDocuments);
    setSelectAll(!selectAll);
  }, [getFiltersDocuments, localDocuments, selectAll]);

  const handleDocumentSelect = useCallback(
    attachmentId => {
      const updatedDocuments = localDocuments.map(doc => {
        if (doc.document.attachmentId === attachmentId) {
          return {
            ...doc,
            document: {
              ...doc.document,
              isSelected: !doc.document.isSelected
            }
          };
        }
        return doc;
      });
      setLocalDocuments(updatedDocuments);
      setSelectAll(false);
    },
    [localDocuments]
  );

  const [bulkChangeDocumentAttachmentState] = useMutation(CHANGE_BULK_DOCUMENT_ATTACHMENT_STATE_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setActionsStates(prev => ({ ...prev, readOnlyAll: true }));
    },
    onCompleted: r => {
      const { correlationId } = r.bulkChangeDocumentAttachmentState;
      storeKey({ correlationId });
    }
  });

  const onOpenChangeStateBulkTransitionMessage = useCallback(() => {
    setShowChangeStateBulkTransitionMessage(true);
  }, []);

  const onCloseChangeStateBulkTransitionMessage = useCallback(() => {
    setShowChangeStateBulkTransitionMessage(false);
  }, []);

  const onYesChangeStateBulkTransitionMessage = useCallback(() => {
    onCloseChangeStateBulkTransitionMessage();

    if (!newStateBulkTransition) return;

    const { documentAttachmentsTransitions, finalStateId } = newStateBulkTransition;
    const transitionsList = documentAttachmentsTransitions.filter(property => property.finalStateId === finalStateId);
    let documentsWithMissingAttachments = [];
    let documentsWithDeletedAttachments = [];
    transitionsList.forEach(property => {
      const document = localDocuments.find(doc => doc.document.attachmentId === property.attachmentId);
      if (document && property.finalStateActiveProduct) {
        for (const prop of document.document.attachmentProperties) {
          if (prop.propertyMandatory && !prop.propertyValue) {
            documentsWithMissingAttachments.push(document.document.attachmentTypeName);
            break;
          }
        }

        if (!document.document.isUploaded && document.document.isDeleted && !property.isInitialOrPostUploadTransition) {
          documentsWithDeletedAttachments.push(document.document.attachmentTypeName);
        }
      }
    });

    if (documentsWithDeletedAttachments.length > 0) {
      addToast(
        t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.ErrorMessage', {
          data: documentsWithDeletedAttachments.join(', ')
        }),
        'error'
      );
      return;
    }

    if (documentsWithMissingAttachments.length > 0) {
      addToast(t('OfferEdit.Documents.MissingAttachmentProperty', { data: documentsWithMissingAttachments.join(', ') }), 'error');
      return;
    }

    const updatedLocalDocuments = localDocuments.map(doc => {
      if (transitionsList.some(property => property.attachmentId === doc.document.attachmentId)) {
        return {
          ...doc,
          document: {
            ...doc.document,
            saving: true,
            isSelected: false
          }
        };
      }
      return doc;
    });

    setSelectAll(false);
    setLocalDocuments(updatedLocalDocuments);

    const inputList = transitionsList.map(property => ({
      documentAttachmentId: property.attachmentId,
      flowId: property.flowId,
      transitionId: property.transitionId,
      stateId: property.finalStateId
    }));

    bulkChangeDocumentAttachmentState({
      variables: {
        input: {
          documentId,
          siteId,
          attachmentsData: inputList
        }
      }
    });

    setNewStateBulkTransition(null);
  }, [
    addToast,
    bulkChangeDocumentAttachmentState,
    documentId,
    localDocuments,
    newStateBulkTransition,
    onCloseChangeStateBulkTransitionMessage,
    siteId,
    t
  ]);

  const handleChangeStateBulkTransitionClick = useCallback(
    (documentAttachmentsTransitions, finalStateId) => () => {
      const onSaveAsync = async () => {
        setNewStateBulkTransition({ documentAttachmentsTransitions, finalStateId });
        onOpenChangeStateBulkTransitionMessage();
      };
      onSaveAsync();
    },
    [onOpenChangeStateBulkTransitionMessage]
  );

  const downloadSelectedFiles = useCallback(
    (fileIds, exportAllDocumentVersions) => {
      function downloadFileAsync() {
        try {
          window.location = getBulkFilesUrl(
            t('OfferEdit.Documents.UploadedDocuments', { documentNumber }),
            fileIds,
            exportAllDocumentVersions
          );
        } catch (error) {
          showError(error.message);
        }
      }
      downloadFileAsync();
    },
    [t, documentNumber]
  );

  const downloadClick = useCallback(
    (fileIds, exportAllDocumentVersions) => {
      downloadSelectedFiles(fileIds, exportAllDocumentVersions);
    },
    [downloadSelectedFiles]
  );

  return (
    <>
      {!documentsLoaded ? (
        <FakeText lines={6} />
      ) : (
        <>
          {showBulkModal && (
            <BulkDocumentsContainer
              documentId={documentId}
              siteId={siteId}
              show={showBulkModal}
              onToggle={onToggleBulkModal}
              documents={localDocuments
                .filter(d => d.canUpload)
                .map(a => ({
                  attachmentId: a.document.attachmentId,
                  attachmentName: `${a.document.attachmentTypeName} (${a.document.entityTitle})`,
                  attachmentTypeId: a.document.attachmentTypeId,
                  contextId: a.document.contextId
                }))}
              saveDocument={onSaveDocument}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          <DocumentsUploadListComponent
            onToggleBulkModal={onToggleBulkModal}
            documents={getLocalDocumentsPaged()}
            entities={entities}
            documentId={documentId}
            siteId={siteId}
            onAdd={onAdd}
            onDelete={onDelete}
            onDeleteFiles={onDeleteFiles}
            changeLocalDocuments={changeLocalDocuments}
            filters={filters}
            onFilterChanged={onFilterChanged}
            resetFilters={resetFilters}
            fullDocumentsList={localDocuments}
            states={getLocalStates()}
            users={getLocalUsersName()}
            selectAll={selectAll}
            onSelectAllDocuments={handleSelectAll}
            onSelectDocument={handleDocumentSelect}
            onBulkTransitionStateChangeClick={handleChangeStateBulkTransitionClick}
            selectedDocumentsCount={localDocuments?.filter(doc => doc.document.isSelected)?.length}
            downloadClick={downloadClick}
            actionsStates={actionsStates}
            setActionsStates={setActionsStates}
            filesUploadInProgress={filesUploadInProgress}
            setFilesUploadInProgress={setFilesUploadInProgress}
          />
          <PaginationStyled
            count={getFiltersDocuments().length}
            pageSize={pager.pageSize}
            page={pager.page - 1}
            onRowsPerPageChange={onChangeRowsPerPage}
            onPageChange={onChangePage}
            onRefresh={handleRefresh}
          />
        </>
      )}
      <DocumentsAvailableUploadListComponent
        showModal={showModal}
        onCloseAvailableUploadListModal={onCloseAvailableUploadListModal}
        onAddUploadDocuments={onAddUploadDocuments}
        onRowSelectionChanged={onRowSelectionChanged}
        onFilterValueChanged={onFilterValueChanged}
        availableUploadDocuments={
          localAvailableUploadDocuments
            ? Object.assign(filterAvailableDocuments(localAvailableUploadDocuments, filter, partners, assets), {
                isLoaded: availableUploadDocumentsLoaded
              })
            : null
        }
        partners={partners}
        assets={assets}
        onPartnerChange={onPartnerChange}
        onAssetChange={onAssetChange}
        filter={filter}
      />

      {showChangeStateBulkTransitionMessage && (
        <MessageBoxModal
          open={showChangeStateBulkTransitionMessage}
          onConfirmation={onYesChangeStateBulkTransitionMessage}
          onCloseModal={onCloseChangeStateBulkTransitionMessage}
          title={t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.Title')}
          text={t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.Content')}
        />
      )}

      {showDeleteAttachmentMessage && (
        <MessageBoxModal
          open={showDeleteAttachmentMessage}
          onConfirmation={onYesDeleteAttachmentMessage}
          onCloseModal={onCloseDeleteAttachmentMessage}
          title={t('OfferEdit.Documents.DeleteAttachmentPopUp.Title')}
          text={t('OfferEdit.Documents.DeleteAttachmentPopUp.Content')}
        />
      )}

      {showDeleteAttachmentFilesMessage && (
        <MessageBoxModal
          open={showDeleteAttachmentFilesMessage}
          onConfirmation={onYesDeleteAttachmentFilesMessage}
          onCloseModal={onCloseDeleteAttachmentFilesMessage}
          title={t('OfferEdit.Documents.DeleteAttachmentPopUp.Title2')}
          text={t(
            attachmentToDelete.deleteMyFiles
              ? 'OfferEdit.Documents.DeleteAttachmentPopUp.ContentMyVersions'
              : 'OfferEdit.Documents.DeleteAttachmentPopUp.ContentAllVersions'
          )}
        />
      )}
    </>
  );
};

DocumentsUploadContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documentNumber: PropTypes.string.isRequired,
  documents: PropTypes.array,
  generated: PropTypes.bool.isRequired,
  documentsLoaded: PropTypes.bool,
  forceReloadDocuments: PropTypes.bool.isRequired,
  availableUploadDocuments: PropTypes.array,
  availableUploadDocumentsLoading: PropTypes.bool,
  partners: PropTypes.array,
  assets: PropTypes.array,
  files: PropTypes.array
};

export default DocumentsUploadContainer;
